import { useEffect, useState } from 'react';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import LogSessionPage from './pages/LogSessionPage';
import { useAuth } from './hooks/useAuth';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { APP_VERSION } from './constants/appVersion';

export default function App() {
  const { user, loading, needsUsername, loginWithGoogle, setUsername, logout, deleteProfile, error } = useAuth();
  const [page, setPage] = useState('login');
  const darkMode = true;
  const [viewingProfile, setViewingProfile] = useState(null);
  const [pendingProfileUid, setPendingProfileUid] = useState(null);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  useEffect(() => {
    if (needsUsername) {
      setPage('login');
      return;
    }
    if (user) {
      setPage((prev) => (prev === 'logSession' ? 'logSession' : 'home'));
    } else {
      setPage('login');
    }
  }, [user, needsUsername]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [page]);

  useEffect(() => {
    const stashPendingProfile = (profileUid) => {
      if (!profileUid) return;
      setPendingProfileUid(profileUid);
      localStorage.setItem('pendingProfileUid', profileUid);
    };

    const openProfileFromQuery = async () => {
      const params = new URLSearchParams(window.location.search);
      const profileUid = params.get('profile');
      if (!profileUid) return;
      stashPendingProfile(profileUid);
      window.history.replaceState({}, '', '/');
    };

    const handleServiceWorkerMessage = (event) => {
      if (event?.data?.type === 'open-profile') {
        stashPendingProfile(event.data.profileUid);
      }
    };

    openProfileFromQuery();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, [user]);

  useEffect(() => {
    const pending = pendingProfileUid || localStorage.getItem('pendingProfileUid');
    if (!pending || !user) return;

    const openPending = async () => {
      try {
        if (pending === user.uid) {
          setViewingProfile(null);
          setPage('profile');
        } else {
          const profileRef = doc(db, 'profiles', pending);
          const profileSnap = await getDoc(profileRef);
          if (profileSnap.exists()) {
            const profileData = profileSnap.data();
            setViewingProfile({ uid: pending, ...profileData });
            setPage('profile');
          }
        }
      } catch (err) {
        console.error('Failed to open pending profile:', err);
      } finally {
        setPendingProfileUid(null);
        localStorage.removeItem('pendingProfileUid');
      }
    };

    openPending();
  }, [pendingProfileUid, user]);

  // Poll server version to detect new deployments
  useEffect(() => {
    let isMounted = true;

    const checkVersion = async () => {
      try {
        const res = await fetch(`/version.json`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (isMounted && data?.version && data.version !== APP_VERSION) {
          setShowUpdateBanner(true);
        }
      } catch (err) {
        // Silent fail to avoid noisy errors
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const handleLogSession = () => {
    setPage('home');
  };

  const handleBackFromUsernameSetup = async () => {
    try {
      await deleteProfile();
      await logout();
    } catch (err) {
      console.error('Failed to delete profile on back:', err);
      logout(); // Still log out even if delete fails
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: darkMode ? '#111827' : '#f3f4f6' }}>
        <p style={{ color: darkMode ? '#ffffff' : '#1f2937', fontWeight: 'bold' }}>Loading...</p>
      </div>
    );
  }

  const pageContent = (() => {
    if (page === 'login') {
      return (
        <LoginPage
          darkMode={darkMode}
          onGoogleLogin={loginWithGoogle}
          onSetUsername={setUsername}
          needsUsername={needsUsername}
          user={user}
          errorMessage={error}
          onDeleteProfile={deleteProfile}
          onBack={handleBackFromUsernameSetup}
        />
      );
    }

    if (page === 'home') {
      return (
        <HomePage
          user={user}
          onLogSession={() => setPage('logSession')}
          onViewProfile={(profile) => {
            setViewingProfile(profile);
            setPage('profile');
          }}
          onBack={logout}
          onLogout={logout}
          onProfile={() => {
            setViewingProfile(null);
            setPage('profile');
          }}
        />
      );
    }

    if (page === 'logSession') {
      return (
        <LogSessionPage
          user={user}
          darkMode={darkMode}
          onCancel={() => setPage('home')}
          onSuccess={handleLogSession}
        />
      );
    }

    if (page === 'profile') {
      return (
        <ProfilePage
          user={user}
          darkMode={darkMode}
          profile={viewingProfile}
          onBack={() => setPage('home')}
          onDeleteProfile={deleteProfile}
          onBackToMain={() => setPage('home')}
        />
      );
    }
  })();

  return (
    <>
      {/* Update Banner */}
      {showUpdateBanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#1f7a1f',
          color: '#ffffff',
          padding: '12px 16px',
          textAlign: 'center',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          fontWeight: 'bold',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          <span>✓ Update available!</span>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: '#ffffff',
              color: '#1f7a1f',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>
      )}
      {/* Page content */}
      <div style={{ paddingTop: showUpdateBanner ? '48px' : '0' }}>
        {pageContent}
      </div>
    </>
  );
}
